import React, { useState } from "react";
import { endOfToday, endOfYesterday, formatISO, parseISO, sub } from "date-fns";

export interface DateRange {
  start: Date;
  end: Date;
  option?: string;
}

const dateStartInputId = "date_start_input";
const dateEndInputId = "date_end_input";

export const dateRangeLast7Days = "7d";
export const dateRangeLast30Days = "30d";
export const dateRangeLast3Months = "3m";
export const dateRangeLast6Months = "6m";
export const dateRangeLast12Months = "12m";
export const dateRangeCustom = "custom";

interface DateRangeInputProps {
  value: DateRange;
  onChange: (dateRange: DateRange) => void;
  defaultOption?: string;
  className?: string;
}

export default function DateRangeInput({
  value,
  onChange,
  defaultOption = dateRangeLast3Months,
  className = "",
}: DateRangeInputProps) {
  const [custom, setCustom] = useState(false);

  return (
    <div className={`flex flex-wrap gap-2 items-start ${className}`}>
      <label htmlFor="date_range" hidden>
        {I18n.t("js.administration.statistics.time_frame")}
      </label>
      <select
        name="date_range"
        id="date_range"
        className="shrink grow basis-[content]"
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
          const selectedDateRangeOptionValue = event.target.value;

          if (selectedDateRangeOptionValue == dateRangeCustom) {
            setCustom(true);
          } else {
            setCustom(false);
            switch (selectedDateRangeOptionValue) {
              case dateRangeLast7Days:
                onChange({
                  ...value,
                  start: sub(endOfToday(), { days: 7 }),
                  end: endOfYesterday(),
                  option: selectedDateRangeOptionValue,
                });
                break;
              case dateRangeLast30Days:
                onChange({
                  ...value,
                  start: sub(endOfToday(), { days: 30 }),
                  end: endOfYesterday(),
                  option: selectedDateRangeOptionValue,
                });
                break;
              case dateRangeLast3Months:
                onChange({
                  ...value,
                  start: sub(endOfYesterday(), { months: 3 }),
                  end: endOfYesterday(),
                  option: selectedDateRangeOptionValue,
                });
                break;
              case dateRangeLast6Months:
                onChange({
                  ...value,
                  start: sub(endOfYesterday(), { months: 6 }),
                  end: endOfYesterday(),
                  option: selectedDateRangeOptionValue,
                });
                break;
              case dateRangeLast12Months:
                onChange({
                  ...value,
                  start: sub(endOfYesterday(), { months: 12 }),
                  end: endOfYesterday(),
                  option: selectedDateRangeOptionValue,
                });
                break;
            }
          }
        }}
        defaultValue={defaultOption}
        required
      >
        <option value={dateRangeLast7Days}>
          {I18n.t("js.administration.statistics.last_7_days")}
        </option>
        <option value={dateRangeLast30Days}>
          {I18n.t("js.administration.statistics.last_30_days")}
        </option>
        <option value={dateRangeLast3Months}>
          {I18n.t("js.administration.statistics.last_3_months")}
        </option>
        <option value={dateRangeLast6Months}>
          {I18n.t("js.administration.statistics.last_6_months")}
        </option>
        <option value={dateRangeLast12Months}>
          {I18n.t("js.administration.statistics.last_12_months")}
        </option>
        <option value={dateRangeCustom}>
          {I18n.t("js.administration.statistics.custom_range")}
        </option>
      </select>
      <label htmlFor={dateStartInputId} hidden>
        {I18n.t("js.administration.statistics.date_start")}
      </label>
      <input
        id={dateStartInputId}
        className="shrink grow basis-[content]"
        type="date"
        readOnly={!custom}
        name="date_start"
        max={formatISO(value.end, { representation: "date" })}
        min="2021-06-01"
        value={formatISO(value.start, { representation: "date" })}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          event.target.value &&
            onChange({
              ...value,
              start: parseISO(event.target.value),
            });
        }}
        required
      />
      <label htmlFor={dateEndInputId} hidden>
        {I18n.t("js.administration.statistics.date_end")}
      </label>
      <input
        id={dateEndInputId}
        className="shrink grow basis-[content]"
        type="date"
        readOnly={!custom}
        name="date_end"
        max={formatISO(endOfYesterday(), { representation: "date" })}
        min={formatISO(value.start, { representation: "date" })}
        value={formatISO(value.end, { representation: "date" })}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          event.target.value &&
            onChange({
              ...value,
              end: parseISO(event.target.value),
            });
        }}
        required
      />
    </div>
  );
}
